import produce from "immer";
import _pickBy from "lodash/pickBy";
import { getEmptyPresentationTemplateItem } from "../../components/organisms/TemplateBuilder/utils";
import { NumberIndexedDictionary, TemplateType } from "../../globalTypes";
import { Tables } from "../../requests/table/types";
import {
  DataTemplateItem,
  PresentationTemplateItem,
  TemplateSystem,
} from "../../requests/templateBuilder/types";
import { dataTemplateItemPLACEHOLDER } from "../../requests/templateBuilder/types/placeholderData";
import {
  CellDataDTO,
  MobileRowLayoutDTO,
} from "../../requests/templates/types";
import * as Global from "../../utils/global";

const fillEmptyItem = (
  parent: number | null,
  topParentId: number | null,
  newId: number,
  emptyTemplateItem: DataTemplateItem,
  tables: Tables
) => {
  const emptyItem = Global.DeepCopy(emptyTemplateItem);
  emptyItem.parent = parent;
  emptyItem.globalId = newId;
  emptyItem.topParentId = topParentId;
  emptyItem.type = "value";
  if (parent !== null) {
    const firstTableKey = parseInt(Object.keys(tables)[0]);
    const table = tables[firstTableKey];
    emptyItem.tableName = table.name;
    emptyItem.tableId = firstTableKey;

    /*
          TODO: hack, protože se mi v TemplateItemEditoru nezmění state po tom co udělám na
          :483 -> nastavení null v column a displayColumn když se změní název tabulky.
          Projevuje se to tak, že změním tabulku u joinu a pořád je tam cislo_subjektu i když by to mělo bejt prázdný
          */
    // const column = table.columns[Object.keys(table.columns)[0]];
    // emptyItem.column = column.columnName;
    emptyItem.column = null;
    emptyItem.displayColumn = null;
  }
  return emptyItem;
};

export const updateTemplateItems = (
  newItem: DataTemplateItem,
  items: NumberIndexedDictionary<DataTemplateItem>,
  pItems: NumberIndexedDictionary<PresentationTemplateItem>,
  emptyTemplateItem: DataTemplateItem,
  tables: Tables,
  idCounter: number,
  type: TemplateType,
  system: TemplateSystem
) => {
  const newItems = { ...items };
  const newPItems = { ...pItems };
  let idChanged = false;

  let item = newItems[newItem.globalId];
  const oldItem = { ...item };

  let joinedItem = newItem;
  while (joinedItem.join != null) {
    joinedItem = items[joinedItem.join];
  }

  const oldPItem = { ...newPItems[joinedItem.globalId] };
  let topItem: DataTemplateItem;

  if (item.topParentId != null) {
    Object.keys(items)
      .map((k) => parseInt(k))
      .forEach((key) => {
        if (items[key].globalId === item.topParentId) {
          topItem = items[key];
        }
      });
  } else {
    topItem = item;
  }

  item = { ...newItem };

  let targetJoin = { ...dataTemplateItemPLACEHOLDER };
  Object.keys(items)
    .map((k) => parseInt(k))
    .forEach((key) => {
      const lItem = items[key];
      if (lItem.globalId === topItem.globalId) {
        targetJoin = lItem;
      }
    });
  if (targetJoin.join !== null) {
    while (targetJoin.globalId !== item.globalId) {
      targetJoin = items[targetJoin.join as number];
    }
  }

  let targetJoinId;
  if (targetJoin.globalId === item.globalId) {
    targetJoin = item;

    if (newItem.type === "join" && oldItem.type !== "join") {
      const newId = idCounter;
      idChanged = true;

      const globalId =
        Object.values(items)
          .map((i) => Number(i.globalId))
          .sort((a, b) => b - a)[0] + 1;

      const emptyItem = fillEmptyItem(
        item.globalId,
        item.topParentId === null ? item.globalId : item.topParentId,
        globalId,
        emptyTemplateItem,
        tables
      );

      newPItems[globalId] = getEmptyPresentationTemplateItem(
        newPItems,
        globalId,
        type,
        system,
        oldPItem.columnOrder
      );
      newItems[emptyItem.globalId] = emptyItem;
      targetJoin.join = emptyItem.globalId;
      delete newPItems[newItem.globalId];
    } else if (newItem.type !== "join" && oldItem.type === "join") {
      let localJoinedItem = { ...newItems[targetJoin.join as number] };
      while (localJoinedItem.join != null) {
        delete newItems[localJoinedItem.globalId as number];
        delete newPItems[localJoinedItem.globalId as number];
        localJoinedItem = { ...newItems[localJoinedItem.join as number] };
      }
      delete newItems[localJoinedItem.globalId as number];
      delete newPItems[localJoinedItem.globalId as number];
      // delete newItems[targetJoin.join as number];
      // delete newPItems[joinedItem.globalId as number];
      targetJoin.join = null;

      newPItems[newItem.globalId] = getEmptyPresentationTemplateItem(
        newPItems,
        newItem.globalId,
        type,
        system,
        oldPItem.columnOrder
      );
    }

    if (newItem.tableName !== oldItem.tableName && newItem.topParentId) {
      item.column = null;
      item.displayColumn = null;
    }

    newItems[item.globalId] = item;

    return {
      newPItems,
      newItems,
      idChanged,
      targetJoinId,
    };
  }
  return {};
};

export const addNewTemplateItem = (
  items: NumberIndexedDictionary<DataTemplateItem>,
  pItems: NumberIndexedDictionary<PresentationTemplateItem>,
  emptyTemplateItem: DataTemplateItem,
  tables: Tables,
  idCounter: number,
  type: TemplateType,
  system: TemplateSystem
) => {
  const newItems = { ...items };
  const newPItems = { ...pItems };

  Object.values(newPItems)
    .sort((a, b) => a.columnOrder - b.columnOrder)
    .forEach((value, index) => {
      newPItems[value.id].columnOrder = index + 1;
    });

  const keysOfNewItems = Object.keys(newItems).sort(
    (a, b) => Number(a) - Number(b)
  );

  // You can't rely on idCounter, so I solve it through the last item
  const idOfNextItem = Number(keysOfNewItems[keysOfNewItems.length - 1]) + 1;

  const emptyItem = fillEmptyItem(
    null,
    null,
    idOfNextItem,
    emptyTemplateItem,
    tables
  );
  const emptyPresentationItem = getEmptyPresentationTemplateItem(
    pItems,
    idOfNextItem,
    type,
    system,
    Object.keys(newPItems).length + 1
  );

  newItems[emptyItem.globalId] = emptyItem;
  newPItems[emptyPresentationItem.id] = emptyPresentationItem;

  return {
    newItems,
    newPItems,
  };
};

export const deleteTemplateItem = (
  key: number,
  items: NumberIndexedDictionary<DataTemplateItem>,
  pItems: NumberIndexedDictionary<PresentationTemplateItem>,
  layout: MobileRowLayoutDTO
) => {
  let joinedItem = items[key];

  const newItems = { ...items };
  const newPItems = { ...pItems };

  while (joinedItem.join != null) {
    joinedItem = items[joinedItem.join];
  }

  let parentItem = joinedItem as DataTemplateItem | undefined;

  while (parentItem !== undefined) {
    delete newItems[parentItem.globalId];
    delete newPItems[parentItem.globalId];
    if (parentItem.parent !== null)
      parentItem = items[parentItem.parent as number];
    else parentItem = undefined;
  }

  delete newItems[key];
  delete newPItems[key];

  Object.values(newPItems)
    .sort((a, b) => a.columnOrder - b.columnOrder)
    .forEach((value, index) => {
      newPItems[value.id].columnOrder = index + 1;
    });

  const newLayout = produce(layout, (draft) => {
    const newLocalLayout = _pickBy(
      layout.layout,
      ({ columnValueIndex }) => columnValueIndex !== key
    );
    draft.layout = newLocalLayout;
  });

  return {
    newItems,
    newPItems,
    newLayout,
  };
};
